import { HAMSSAH_CONTACT } from './hamssah-contact.js';

export const STOREFRONT_SITE_SETTINGS_KEY = 'storefront.site.settings';

export const DEFAULT_FREE_SHIPPING_THRESHOLD_MAD = 800;

export type StorefrontSiteSettings = {
  legalName: string;
  displayName: string;
  addressLine1: string;
  city: string;
  country: string;
  email: string;
  ordersEmail: string;
  phone: string;
  phoneDisplay: string;
  whatsapp: string;
  ice: string;
  rc: string;
  deliverySlaDays: string;
  maxFulfillmentDays: number;
  withdrawalDays: number;
  refundDays: number;
  /** Cart + promo banner free-shipping threshold (MAD TTC). */
  freeShippingThresholdMad: number;
  seoTitleDefault: string;
  seoDescriptionDefault: string;
  socialInstagram?: string;
  socialFacebook?: string;
  socialTiktok?: string;
  supportWhatsapp?: string;
  legalCgvIntro?: string;
  legalPrivacyIntro?: string;
  legalCookiesIntro?: string;
  legalReturnsIntro?: string;
  legalDeliveryIntro?: string;
};

export const DEFAULT_STOREFRONT_SITE_SETTINGS: StorefrontSiteSettings = {
  legalName: HAMSSAH_CONTACT.legalName,
  displayName: HAMSSAH_CONTACT.displayName,
  addressLine1: HAMSSAH_CONTACT.addressLine1,
  city: HAMSSAH_CONTACT.city,
  country: HAMSSAH_CONTACT.country,
  email: HAMSSAH_CONTACT.email,
  ordersEmail: HAMSSAH_CONTACT.ordersEmail,
  phone: HAMSSAH_CONTACT.phone,
  phoneDisplay: HAMSSAH_CONTACT.phoneDisplay,
  whatsapp: HAMSSAH_CONTACT.whatsapp,
  ice: HAMSSAH_CONTACT.ice,
  rc: HAMSSAH_CONTACT.rc,
  deliverySlaDays: HAMSSAH_CONTACT.deliverySlaDays,
  maxFulfillmentDays: HAMSSAH_CONTACT.maxFulfillmentDays,
  withdrawalDays: HAMSSAH_CONTACT.withdrawalDays,
  refundDays: HAMSSAH_CONTACT.refundDays,
  freeShippingThresholdMad: DEFAULT_FREE_SHIPPING_THRESHOLD_MAD,
  seoTitleDefault: 'Parapharmacie et cosmétique au Maroc',
  seoDescriptionDefault:
    'Boutique en ligne Hamssah : cosmétique, parapharmacie et conseils experts. Livraison au Maroc.',
};

function coercePositiveInt(value: unknown, fallback: number): number {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n) || n <= 0) return fallback;
  return Math.round(n);
}

export function mergeStorefrontSiteSettings(
  patch: Partial<StorefrontSiteSettings> | null | undefined,
): StorefrontSiteSettings {
  if (!patch || typeof patch !== 'object') return { ...DEFAULT_STOREFRONT_SITE_SETTINGS };
  const merged = {
    ...DEFAULT_STOREFRONT_SITE_SETTINGS,
    ...patch,
  };
  merged.freeShippingThresholdMad = coercePositiveInt(
    merged.freeShippingThresholdMad,
    DEFAULT_FREE_SHIPPING_THRESHOLD_MAD,
  );
  merged.withdrawalDays = coercePositiveInt(merged.withdrawalDays, HAMSSAH_CONTACT.withdrawalDays);
  merged.refundDays = coercePositiveInt(merged.refundDays, HAMSSAH_CONTACT.refundDays);
  merged.maxFulfillmentDays = coercePositiveInt(
    merged.maxFulfillmentDays,
    HAMSSAH_CONTACT.maxFulfillmentDays,
  );
  return merged;
}

export function parseStorefrontSiteSettings(raw: string | null | undefined): StorefrontSiteSettings {
  if (!raw?.trim()) return { ...DEFAULT_STOREFRONT_SITE_SETTINGS };
  try {
    const parsed = JSON.parse(raw) as Partial<StorefrontSiteSettings>;
    return mergeStorefrontSiteSettings(parsed);
  } catch {
    return { ...DEFAULT_STOREFRONT_SITE_SETTINGS };
  }
}

/** Header promo strip — numbers from site settings (same SSOT as cart / legal). */
export function formatStorefrontPromoBannerFr(settings: StorefrontSiteSettings): string {
  const threshold = settings.freeShippingThresholdMad;
  const returns = settings.withdrawalDays;
  return `Livraison offerte dès ${threshold} MAD · Retours ${returns} jours · Paiement sécurisé`;
}

export function siteSettingsHasPlaceholders(settings: StorefrontSiteSettings): string[] {
  const issues: string[] = [];
  if (/^\+2126?0{6,}/.test(settings.phone.replace(/\D/g, ''))) {
    issues.push('Téléphone placeholder détecté');
  }
  if (/^0+$/.test(settings.ice.replace(/\D/g, ''))) {
    issues.push('ICE placeholder détecté');
  }
  if (!settings.email.trim()) issues.push('E-mail contact manquant');
  return issues;
}
